// Import from libraries
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Import custom components & services
import CustomModal from '../../../blocks/custom-modal';
import InfoBox from '../../../blocks/messages/info-box';
import PaymentsService from '../../../../services/PaymentsService';

/**
 * EditPaymentModal renders a modal for editing a payment amount.
 * UX mirrors the creation modal: validate → open confirmation → persist.
 *
 * @param {boolean} open - Determines if the modal is open
 * @param {function} onClose - Handler to close the modal
 * @param {object|null} payment - The payment object to edit (expects at least { id, amount, payment_mode, payment_date })
 * @param {function} onSaved - Callback fired after successful update (e.g., to refresh parent list)
 * @returns {JSX.Element}
 */
const EditPaymentModal = ({ open, onClose, payment, onSaved }) => {
  /** Local states */
  const [amount, setAmount] = useState('');
  const [error, setError] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  /** Sync form with incoming payment */
  useEffect(() => {
    if (payment && typeof payment.amount === 'number') {
      setAmount(String(payment.amount));
    } else {
      setAmount('');
    }
    setError(null);
    setIsConfirmationOpen(false);
  }, [payment, open]);

  /** Format date for display (fr-FR) */
  const formatDateTime = (dateLike) => {
    if (!dateLike) return '-';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('fr-FR');
  };

  /** Validate and open confirmation */
  const handleValidate = () => {
    if (!payment?.id) {
      setError("Le règlement à modifier est introuvable.");
      return;
    }
    if (amount === '') {
      setError('Veuillez renseigner un montant.');
      return;
    }
    const parsed = parseFloat(amount);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Le montant saisi est invalide.');
      return;
    }
    setError(null);
    setIsConfirmationOpen(true);
  };

  /** Confirm and persist */
  const handleConfirm = async () => {
    try {
      const parsed = parseFloat(amount);
      const response = await PaymentsService.editPayment(payment.id, { amount: parsed });
      if (!response) {
        setError("Une erreur est survenue lors de la mise à jour du paiement.");
        return;
      }

      /** Success: notify parent, reset, close */
      if (typeof onSaved === 'function') onSaved();
      setIsConfirmationOpen(false);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Une erreur inattendue est survenue');
    }
  };

  /** Cancel confirmation only */
  const handleCancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  return (
    <React.Fragment>
      {/* Main edit modal */}
      <CustomModal
        open={open}
        onClose={onClose}
        onCloseLabel="Annuler"
        title="Modifier le montant du règlement"
        handleSave={handleValidate}
        handleSaveLabel="Valider"
      >
        <div className="edit-payment-modal-content">
          {error && <InfoBox text={error} type="error" />}
          <p className="mb-8">
            <strong>Référence :</strong> {payment?.id || '—'}
            {' — '}
            <strong>Date :</strong> {formatDateTime(payment?.payment_date)}
          </p>

          <label htmlFor="edit-payment-amount">Nouveau montant (€)</label>
          <input
            id="edit-payment-amount"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Saisir le montant"
          />

          <InfoBox
            className="mt-8"
            type="info"
            text="Seul le montant du règlement est modifiable."
          />
        </div>
      </CustomModal>

      {/* Confirmation modal */}
      {isConfirmationOpen && (
        <CustomModal
          open={isConfirmationOpen}
          onClose={handleCancelConfirmation}
          onCloseLabel="Annuler"
          title="Confirmer la modification"
          handleSave={handleConfirm}
          handleSaveLabel="Confirmer"
        >
          <div className="confirmation-modal-content">
            <InfoBox
              text={
                <React.Fragment>
                  <p className="w-100">Vous êtes sur le point de modifier le règlement :</p>
                  <ul className="w-100">
                    <li><strong>ID :</strong> {payment?.id || '—'}</li>
                    <li><strong>Nouveau montant :</strong> {amount} €</li>
                    <li><strong>Date d’origine :</strong> {formatDateTime(payment?.payment_date)}</li>
                  </ul>
                  <strong>Cette action ne pourra pas être annulée. Confirmez-vous ?</strong>
                </React.Fragment>
              }
              type="warning"
              disabledCloseBtn={true}
            />
          </div>
        </CustomModal>
      )}
    </React.Fragment>
  );
};

EditPaymentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  payment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amount: PropTypes.number,
    payment_mode: PropTypes.string,
    payment_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }),
};

export default EditPaymentModal;
